import { useNavigate, useParams } from 'react-router-dom'
import { Button, buttonActionIcon, Input } from '../ui'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Modal } from '../ui/modal'
import { Header, SubHeader } from '../ui/header'
import { cn } from '../../lib/utils'
import { logo } from '../../components/ui/logo'

interface ResetPasswordProps {
  onResetPassword: (password: string, token: string) => Promise<boolean>
  title?: string
  redirectPath?: string
  variant?: 'user' | 'admin'
  onClearSession?: () => void
}

export function ResetPassword({
  onResetPassword,
  title = 'Reset your password',
  redirectPath = '/',
  variant = 'user',
  onClearSession,
}: ResetPasswordProps) {
  const navigate = useNavigate()
  const { token } = useParams()

  const [state, setState] = useState<'success' | 'error' | 'loading' | 'action' | 'disabled'>(
    'disabled',
  )
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  useEffect(() => {
    if (token === undefined) {
      navigate(redirectPath, { replace: true })
    }
  }, [navigate, token, redirectPath])

  // Clear any existing session when landing on reset password page
  useEffect(() => {
    if (onClearSession) {
      onClearSession()
    }
  }, [onClearSession])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setState('loading')
    try {
      if (password !== confirmPassword) {
        toast.error('Passwords do not match')
        setState('error')
        return
      }
      if (await onResetPassword(password, token ?? '')) {
        toast.success('Password reset successfully')
        setState('success')
        navigate(redirectPath, { replace: true })
      } else {
        toast.error('Invalid')
        setState('error')
      }
    } catch (e) {
      toast.error((e as Error).message)
      setState('error')
    }
  }
  useEffect(() => {
    if (password.length > 0 && confirmPassword.length > 0) {
      setState((prevState) => (prevState !== 'action' ? 'action' : prevState))
    } else {
      setState((prevState) => (prevState !== 'disabled' ? 'disabled' : prevState))
    }
  }, [confirmPassword, password])

  const buttonVariant = useMemo(() => {
    switch (state) {
      case 'action':
      case 'success':
        return 'square_active'
      case 'disabled':
      case 'loading':
        return 'square_disabled'
      case 'error':
        return 'square_error'
    }
  }, [state])

  const LogoComponent = logo({})

  return (
    <Modal LogoComponent={LogoComponent} className="relative">
      {variant === 'admin' && (
        <div className="absolute -top-14 left-0 h-[40px] flex items-center justify-center w-full md:bg-white rounded-2xl">
          <span className="text-main text-[16px] font-semibold border border-main px-8 md:border-none rounded-2xl">
            Admin Portal
          </span>
        </div>
      )}
      <Header title={title} />
      <SubHeader title="Enter your new password" />
      <form onSubmit={handleSubmit} className="grid gap-4">
        <div className="grid gap-6">
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <div className="relative">
            <Input
              id="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              placeholder="Confirm"
              className="pr-14"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Button type="submit" variant={buttonVariant} size="icon" onClick={handleSubmit}>
                <div className={cn(state === 'loading' && 'animate-spin')}>
                  {buttonActionIcon(state)}
                </div>
              </Button>
            </div>
          </div>
        </div>
      </form>
    </Modal>
  )
}
